import { ApiError } from '../services/types';

/**
 * Making sure the server hears that a call is over.
 *
 * ---------------------------------------------------------------------------
 * THE GHOST CALL
 * ---------------------------------------------------------------------------
 * Hanging up did two things: it tore the screen down, and it told the server.
 * The second one was allowed to fail silently, on the reasoning that `/end` is
 * idempotent and both ends race to call it - so a failure is "almost always"
 * the loser of that race, reporting a hang-up the winner already reported.
 *
 * That reasoning is right about the race and wrong about everything else. A
 * request that **never reached the server** fails identically to one that
 * arrived second, and the two could not be less alike:
 *
 *   lost the race   the call is already ended. Nothing to do.
 *   never arrived   the call is still live, server-side, and nobody knows.
 *
 * In the second case the screen said "Call ended" and the platform went on
 * believing the person was mid-conversation. The next time they tried to ring
 * anybody they were refused with *"You are already on a call"* - about a call
 * that had finished minutes ago - and the only ways out were tapping the
 * stale-call escape hatch or waiting for the reaper's sweep.
 *
 * ---------------------------------------------------------------------------
 * THE SCREEN NEVER WAITS FOR THIS
 * ---------------------------------------------------------------------------
 * {@link ensureCallEnded} resolves on the **first** attempt, whatever it does,
 * and any retries run behind it unwatched. That split is the point: the call is
 * over for the person the instant they press the button, and whether the server
 * has been told yet is not their problem and must never look like one. Awaiting
 * the retries instead would leave the ending screen up for a quarter of a
 * minute on exactly the connection that was already struggling.
 */

/** Three background attempts after the first: 2s, 4s, 8s. */
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000];

/**
 * Whether it is worth asking again.
 *
 * `isNetwork` covers no signal, a dead tunnel and a timeout - the request never
 * got a reply, so the server's view of the call is unknown and probably stale.
 * A `5xx` means it arrived and the server fell over, which is equally worth a
 * second try. Anything else is an **answer** rather than a failure: already
 * ended, not yours, no such call. Asking again produces the same answer more
 * slowly.
 */
const worthRetrying = (error: unknown): boolean =>
  error instanceof ApiError && (error.isNetwork || error.status >= 500);

async function keepTelling<T>(end: () => Promise<T>): Promise<void> {
  for (const delay of RETRY_DELAYS_MS) {
    await new Promise<void>(resolve => setTimeout(() => resolve(), delay));
    try {
      await end();
      return;
    } catch (error) {
      if (!worthRetrying(error)) return;
    }
  }
  /*
   * Out of attempts. This stops silently rather than surfacing anything: the
   * screen moved on long ago, and there is a visible way back for a call the
   * server still thinks is live - the stale-call action on the call button,
   * which uses the blocking call id the eligibility check returns.
   */
}

/**
 * Ends a call, and keeps trying in the background if the server may not have
 * heard.
 *
 * Resolves as soon as the first attempt settles - with the server's answer, or
 * `null` if that attempt failed. A `null` does not mean the call is still open;
 * it means this app does not yet know, and is finding out on its own time.
 */
export async function ensureCallEnded<T>(
  end: () => Promise<T>,
): Promise<T | null> {
  try {
    return await end();
  } catch (error) {
    if (worthRetrying(error)) void keepTelling(end);
    return null;
  }
}
